import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowCounterClockwise } from '@phosphor-icons/react/dist/csr/ArrowCounterClockwise';
import { Info } from '@phosphor-icons/react/dist/csr/Info';
import {
  calculateNEWS2,
  getOxygenSaturationBands,
  PULSE_BANDS,
  RESPIRATION_BANDS,
  SYSTOLIC_BANDS,
  TEMPERATURE_BANDS,
} from './constants';
import { ClinicalSituation, ConsciousLevel, NEWS2State, OxygenScale, OxygenTherapy, SituationId } from './types';
import ScoreDisplay from './components/ScoreDisplay';
import SituationScenarioCard from './components/SituationScenarioCard';
import SituationsPanel from './components/SituationsPanel';
import VitalInput, { getScoreColor } from './components/VitalInput';
import { getSituationById } from './situations';

const INITIAL_VITALS: NEWS2State = {
  respirationRate: 16,
  oxygenSaturation: 98,
  airOrOxygen: 'Air',
  systolicBP: 120,
  pulseRate: 72,
  consciousness: ConsciousLevel.ALERT,
  temperature: 37,
  oxygenScale: OxygenScale.SCALE_1,
};

const isCompactViewport = () =>
  typeof window !== 'undefined' && window.matchMedia('(max-width: 760px)').matches;

const App: React.FC = () => {
  const [vitals, setVitals] = useState<NEWS2State>(INITIAL_VITALS);
  const [isSituationsOpen, setIsSituationsOpen] = useState(false);
  const [selectedSituationId, setSelectedSituationId] = useState<SituationId | null>(null);
  const [footerOpen, setFooterOpen] = useState(() => !isCompactViewport());
  const situationsButtonRef = useRef<HTMLButtonElement>(null);
  const situationScenarioRef = useRef<HTMLElement>(null);
  const result = useMemo(() => calculateNEWS2(vitals), [vitals]);
  const activeSituation: ClinicalSituation | undefined = useMemo(
    () => getSituationById(selectedSituationId),
    [selectedSituationId],
  );
  const oxygenBands = useMemo(
    () => getOxygenSaturationBands(vitals.oxygenScale, vitals.airOrOxygen),
    [vitals.airOrOxygen, vitals.oxygenScale],
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 760px)');
    const syncFooterState = () => setFooterOpen(!mediaQuery.matches);

    syncFooterState();
    mediaQuery.addEventListener('change', syncFooterState);
    return () => mediaQuery.removeEventListener('change', syncFooterState);
  }, []);

  useEffect(() => {
    if (!activeSituation) return;
    window.requestAnimationFrame(() => situationScenarioRef.current?.focus());
  }, [activeSituation]);

  const updateVital = <K extends keyof NEWS2State>(key: K, value: NEWS2State[K]) => {
    setVitals((current) => ({ ...current, [key]: value }));
  };

  const closeSituations = () => {
    setIsSituationsOpen(false);
    window.requestAnimationFrame(() => situationsButtonRef.current?.focus());
  };

  const selectSituation = (id: SituationId) => {
    setSelectedSituationId(id);
    setIsSituationsOpen(false);
  };

  const resetCalculator = () => {
    setVitals(INITIAL_VITALS);
    setSelectedSituationId(null);
    setIsSituationsOpen(false);
  };

  const clearSituation = () => {
    setSelectedSituationId(null);
    window.requestAnimationFrame(() => situationsButtonRef.current?.focus());
  };

  const focusCalculator = () => {
    const firstInput = document.getElementById('respiration-rate-number');
    if (!(firstInput instanceof HTMLInputElement)) return;

    firstInput.scrollIntoView?.({ block: 'center' });
    firstInput.focus();
  };

  const renderSegmentedButton = <T extends string>(
    value: T,
    currentValue: T,
    label: string,
    onSelect: (nextValue: T) => void,
  ) => (
    <button
      key={value}
      type="button"
      aria-pressed={currentValue === value}
      onClick={() => onSelect(value)}
    >
      {label}
    </button>
  );

  const oxygenControls = (
    <div className="inline-controls">
      <div className="inline-control-group">
        <div className="inline-control-label">
          <span>SpO₂ skalė</span>
          <span className="tooltip">
            <button type="button" aria-label="Kada naudojama SpO₂ 2 skalė">
              <Info size={16} weight="fill" aria-hidden="true" />
            </button>
            <span role="tooltip">
              2 skalė naudojama tik patvirtinus hiperkapninį kvėpavimo nepakankamumą ir
              paskyrus tikslinę 88–92 % saturaciją.
            </span>
          </span>
        </div>
        <div className="segmented-control" aria-label="SpO₂ skalė">
          {renderSegmentedButton(
            OxygenScale.SCALE_1,
            vitals.oxygenScale,
            '1 skalė',
            (value) => updateVital('oxygenScale', value),
          )}
          {renderSegmentedButton(
            OxygenScale.SCALE_2,
            vitals.oxygenScale,
            '2 skalė',
            (value) => updateVital('oxygenScale', value),
          )}
        </div>
      </div>

      <div className="inline-control-group">
        <span className="inline-control-label">Terapija</span>
        <div className="segmented-control" aria-label="Deguonies terapija">
          {renderSegmentedButton<OxygenTherapy>(
            'Air',
            vitals.airOrOxygen,
            'Oras',
            (value) => updateVital('airOrOxygen', value),
          )}
          {renderSegmentedButton<OxygenTherapy>(
            'Oxygen',
            vitals.airOrOxygen,
            'Deguonis',
            (value) => updateVital('airOrOxygen', value),
          )}
        </div>
        {vitals.airOrOxygen === 'Oxygen' ? (
          <span className="oxygen-supplement">+2 balai už papildomą O₂</span>
        ) : null}
      </div>
    </div>
  );

  return (
    <div className="app-shell">
      <header className="app-header">
        <h1>NEWS2 skaičiuoklė</h1>
        <div className="app-header__actions">
          <button
            ref={situationsButtonRef}
            className="situations-trigger"
            type="button"
            aria-expanded={isSituationsOpen}
            aria-controls="situations-panel"
            onClick={() => setIsSituationsOpen((open) => !open)}
          >
            Situacijos
          </button>
          <button className="reset-button" type="button" onClick={resetCalculator}>
            <ArrowCounterClockwise size={20} weight="bold" aria-hidden="true" />
            Atstatyti
          </button>
        </div>
      </header>

      <SituationsPanel
        open={isSituationsOpen}
        selectedId={selectedSituationId}
        onSelect={selectSituation}
        onClose={closeSituations}
      />

      <main className="app-main">
        {activeSituation ? (
          <SituationScenarioCard
            ref={situationScenarioRef}
            situation={activeSituation}
            onGoToCalculator={focusCalculator}
            onChange={() => setIsSituationsOpen(true)}
            onClear={clearSituation}
          />
        ) : null}

        <div className="app-layout">
          <section className="worksheet" aria-label="NEWS2 parametrai">
          <div className="worksheet__header" aria-hidden="true">
            <span>Parametras</span>
            <span>Vertė</span>
            <span>NEWS2 balo skalė</span>
          </div>

          <VitalInput
            id="respiration-rate"
            label="Kvėpavimo dažnis"
            unit="k./min"
            value={vitals.respirationRate}
            min={0}
            max={60}
            bands={RESPIRATION_BANDS}
            onChange={(value) => updateVital('respirationRate', value)}
          />

          <VitalInput
            id="oxygen-saturation"
            label="Deguonies saturacija"
            unit="%"
            value={vitals.oxygenSaturation}
            min={70}
            max={100}
            bands={oxygenBands}
            controls={oxygenControls}
            variant="oxygen"
            onChange={(value) => updateVital('oxygenSaturation', value)}
          />

          <VitalInput
            id="systolic-bp"
            label="Sistolinis AKS"
            unit="mmHg"
            value={vitals.systolicBP}
            min={40}
            max={250}
            bands={SYSTOLIC_BANDS}
            onChange={(value) => updateVital('systolicBP', value)}
          />

          <VitalInput
            id="pulse-rate"
            label="Pulsas"
            unit="k./min"
            value={vitals.pulseRate}
            min={20}
            max={220}
            bands={PULSE_BANDS}
            onChange={(value) => updateVital('pulseRate', value)}
          />

          <div className="vital-row vital-row--consciousness">
            <div className="vital-row__label">
              <span id="consciousness-label">Sąmonė</span>
            </div>
            <div className="vital-row__value">
              <div className="segmented-control" aria-labelledby="consciousness-label">
                {renderSegmentedButton(
                  ConsciousLevel.ALERT,
                  vitals.consciousness,
                  'Budrus',
                  (value) => updateVital('consciousness', value),
                )}
                {renderSegmentedButton(
                  ConsciousLevel.VPU,
                  vitals.consciousness,
                  'CVPU',
                  (value) => updateVital('consciousness', value),
                )}
              </div>
            </div>
            <div className="vital-row__scale">
              <div className="categorical-scale" aria-hidden="true">
                <div>
                  <span>0</span>
                  <i style={{ backgroundColor: getScoreColor(0) }} />
                  <small>A</small>
                </div>
                <div>
                  <span>3</span>
                  <i style={{ backgroundColor: getScoreColor(3) }} />
                  <small>C, V, P arba U</small>
                </div>
              </div>
            </div>
          </div>

          <VitalInput
            id="temperature"
            label="Temperatūra"
            unit="°C"
            value={vitals.temperature}
            min={30}
            max={45}
            step={0.1}
            bands={TEMPERATURE_BANDS}
            onChange={(value) => updateVital('temperature', value)}
          />
          </section>

          <ScoreDisplay result={result} />
        </div>
      </main>

      <footer className="app-footer">
        <p className="app-footer__warning">
          Informacija skirta tik medicinos specialistams. NEWS2 negali pakeisti gydytojo klinikinio sprendimo.
        </p>
        <details
          className="app-footer__sources"
          open={footerOpen}
          onToggle={(event) => setFooterOpen(event.currentTarget.open)}
        >
          <summary>RCP priskyrimas ir vertimo perspėjimas</summary>
          <div className="app-footer__source-content">
            <p>
              Reproduced from: Royal College of Physicians. National Early Warning Score (NEWS) 2:
              Standardising the assessment of acute-illness severity in the NHS. Updated report of a working
              party. London: RCP, 2017.
            </p>
            <p>
              The wording of this translation has not been specifically approved by the Royal College of
              Physicians – please refer to the English language version before making any clinical use of this
              information.
            </p>
          </div>
        </details>
      </footer>
    </div>
  );
};

export default App;
